/**
 * Mailboxes tab - IMAP config for polling cases@sonicmail.net.au (Settings → Case Module)
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Mail as MailIcon } from '@mui/icons-material';
import apiService from '../services/api';

interface MailboxesTabProps {
  onError?: (msg: string) => void;
}

const MailboxesTab: React.FC<MailboxesTabProps> = ({ onError }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    imap_server: 'imap.sonicmail.net.au',
    imap_port: 993,
    imap_use_ssl: true,
    username: 'cases@sonicmail.net.au',
    password: '',
    is_enabled: false,
    poll_interval_seconds: 60,
  });
  const [passwordSet, setPasswordSet] = useState(false);
  const [lastPolledAt, setLastPolledAt] = useState<string | null>(null);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const config = await apiService.getCaseMailboxConfig();
      setForm((f) => ({
        ...f,
        imap_server: config.imap_server || 'imap.sonicmail.net.au',
        imap_port: config.imap_port ?? 993,
        imap_use_ssl: config.imap_use_ssl ?? true,
        username: config.username || '',
        is_enabled: config.is_enabled ?? false,
        poll_interval_seconds: config.poll_interval_seconds ?? 60,
        password: '',
      }));
      setPasswordSet(config.password_set ?? false);
      setLastPolledAt(config.last_polled_at ?? null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  // Refresh status periodically when polling is enabled
  useEffect(() => {
    if (!form.is_enabled) return;
    const t = setInterval(fetchConfig, 30000);
    return () => clearInterval(t);
  }, [form.is_enabled]);

  const handleSave = async () => {
    if (!form.imap_server.trim() || !form.username.trim()) {
      setError('Server and username are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiService.updateCaseMailboxConfig({
        imap_server: form.imap_server.trim(),
        imap_port: form.imap_port,
        imap_use_ssl: form.imap_use_ssl,
        username: form.username.trim(),
        password: form.password.trim() || undefined,
        is_enabled: form.is_enabled,
        poll_interval_seconds: Math.max(10, form.poll_interval_seconds),
      });
      setForm((f) => ({ ...f, password: '' }));
      setPasswordSet(true);
      fetchConfig();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Case mailbox</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Poll cases@sonicmail.net.au for customer replies. Emails with case number (e.g. CS-000007) in the subject
        are added to the case activity timeline and removed from the server.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: 3, maxWidth: 480 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="IMAP server"
            value={form.imap_server}
            onChange={(e) => setForm({ ...form, imap_server: e.target.value })}
            fullWidth
            placeholder="imap.sonicmail.net.au"
          />
          <TextField
            label="Port"
            type="number"
            value={form.imap_port}
            onChange={(e) => setForm({ ...form, imap_port: parseInt(e.target.value, 10) || 993 })}
            fullWidth
            inputProps={{ min: 1, max: 65535 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.imap_use_ssl}
                onChange={(e) => setForm({ ...form, imap_use_ssl: e.target.checked })}
              />
            }
            label="Use SSL"
          />
          <TextField
            label="Username"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            fullWidth
            placeholder="cases@sonicmail.net.au"
          />
          <TextField
            label="Password"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            fullWidth
            placeholder={passwordSet ? '•••••••• (leave blank to keep existing)' : 'Enter password'}
            helperText={passwordSet ? 'Leave blank to keep existing password' : ''}
          />
          <TextField
            label="Poll interval (seconds)"
            type="number"
            value={form.poll_interval_seconds}
            onChange={(e) => setForm({ ...form, poll_interval_seconds: parseInt(e.target.value, 10) || 60 })}
            fullWidth
            inputProps={{ min: 10 }}
            helperText="Minimum 10 seconds. How often to check for new emails."
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.is_enabled}
                onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
              />
            }
            label="Enable polling"
          />
          {form.is_enabled && (
            <Typography variant="body2" color="text.secondary">
              {lastPolledAt
                ? `Last polled: ${new Date(lastPolledAt).toLocaleString()}`
                : 'Polling will start on next cycle.'}
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <MailIcon />}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default MailboxesTab;
